import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useObserver } from 'mobx-react-lite';
import useUser from 'src/hooks/use-user';
import { useModals, ModalNames } from 'src/components/modals';

import { sortSpecialCards } from 'shared/helpers/specials';
import { Typography } from 'src/typography';
import { BreadCrumbs } from 'src/components/bread-crumbs';
import { useIsMobile } from 'src/hooks/use-is-mobile';
import { SpecialOfferCard } from 'src/components/special-offer-card';
import useUI from 'src/hooks/use-ui';
import { Dispensaries, SpecialMenuCard } from 'src/gql/graphql';

const SKELETON_CARD_COUNT = 24;

type OfferMenuSectionV2Props = {
  dispensary: Dispensaries;
  specials: SpecialMenuCard[];
  pageTitle: string;
  pageDescription?: string;
  loading?: boolean;
};

export function OfferMenuSectionV2({
  dispensary,
  specials,
  pageTitle,
  pageDescription,
  loading = false,
}: OfferMenuSectionV2Props): JSX.Element | null {
  const isMobile = useIsMobile();
  const { isDutchieMain, dispensaryRootUrl } = useUI();
  const { openModal } = useModals();
  const User = useUser();
  const isLoggedIn = useObserver(() => User.isLoggedIn);

  const sortedByExpiration = useMemo(() => sortSpecialCards(specials), [specials]);
  const hasRestrictedSpecials = useMemo(() => specials.some((special) => special.isRestricted), [specials]);
  const showLogin = !isLoggedIn && hasRestrictedSpecials;

  const getSpecialUrl = (special: SpecialMenuCard): string | null => {
    const { _id, specialType } = special;
    if (!_id || !specialType) {
      return null;
    }
    const specialTypeSubRoute = specialType === `sale` ? specialType : `offer`;

    return `/${dispensaryRootUrl}/${dispensary.cName}/specials/${specialTypeSubRoute}/${_id}`;
  };

  const handleLoginClick = (): void => {
    void openModal(ModalNames.login);
  };

  if (loading) {
    return (
      <Section>
        {isDutchieMain && (
          <BreadCrumbsContainer $isMobile={isMobile}>
            <BreadCrumbs />
          </BreadCrumbsContainer>
        )}

        <Typography.Heading size='medium' tag='h1'>
          {pageTitle}
        </Typography.Heading>

        <SpecialsGridContainer
          role='status'
          aria-live='polite'
          aria-busy='true'
          aria-label='Loading specials'
          data-testid='offer-menu-section-v2-loading'
        >
          {Array.from({ length: SKELETON_CARD_COUNT }).map((_val, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <SkeletonCard key={index} aria-hidden='true'>
              <SkeletonImage />
              <SkeletonText>
                <SkeletonLine $width='80%' />
                <SkeletonLine $width='55%' />
                <SkeletonLine $width='40%' />
              </SkeletonText>
            </SkeletonCard>
          ))}
        </SpecialsGridContainer>
      </Section>
    );
  }

  if (specials.length === 0) {
    return null;
  }

  return (
    <Section>
      {isDutchieMain && (
        <BreadCrumbsContainer $isMobile={isMobile}>
          <BreadCrumbs />
        </BreadCrumbsContainer>
      )}

      <Typography.Heading size='medium' tag='h1'>
        {pageTitle}
      </Typography.Heading>

      {pageDescription && <Label>{pageDescription}</Label>}

      {showLogin && (
        <LoginPrompt>
          <LoginIcon>
            <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M10 10C12.7614 10 15 7.76142 15 5C15 2.23858 12.7614 0 10 0C7.23858 0 5 2.23858 5 5C5 7.76142 7.23858 10 10 10Z'
                fill='currentColor'
              />
              <path
                d='M10 12C4.47715 12 0 14.4772 0 17.5C0 18.8807 1.11929 20 2.5 20H17.5C18.8807 20 20 18.8807 20 17.5C20 14.4772 15.5228 12 10 12Z'
                fill='currentColor'
              />
            </svg>
          </LoginIcon>
          <LoginText>Sign in to see exclusive member offers</LoginText>
          <LoginButton onClick={handleLoginClick} data-testid='specials-login-button'>
            Sign In
          </LoginButton>
        </LoginPrompt>
      )}

      <SpecialsGridContainer>
        {sortedByExpiration.map((special) => {
          const specialUrl = getSpecialUrl(special);
          return specialUrl ? (
            <SpecialOfferCard key={special._id} special={special} href={specialUrl} inGridLayout />
          ) : null;
        })}
      </SpecialsGridContainer>
    </Section>
  );
}

const Label = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[35]};
  line-height: 30px;
  margin: 0 0 14px;
  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding-left: 25px;
    margin: 0;
  }
`;

const SpecialsGridContainer = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr;
  margin: 15px 0 16px;
  padding: 0 20px 15px;
  width: 100%;

  > a {
    min-width: 0;
  }

  @media (min-width: 600px) {
    gap: 16px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 960px) {
    padding: 0;
  }
`;

const Section = styled.div`
  margin-top: 44px;
  @media (min-width: 960px) and (max-width: 1280px) {
    padding-left: 20px;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    margin-top: unset;
  }
`;

const BreadCrumbsContainer = styled.div<{ $isMobile: boolean }>`
  margin: ${({ $isMobile }) => ($isMobile ? `25px 0 -25px` : `-18px 0 24px -25px`)};
`;

const LoginPrompt = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #f0f9f5 0%, #e3f3eb 100%);
  border: 1px solid #c7e5d6;
  border-radius: 8px;
  padding: 16px 20px;
  margin: 16px 0 24px;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    margin: 16px 20px 24px;
    flex-wrap: wrap;
    justify-content: center;
    text-align: center;
  }
`;

const LoginIcon = styled.div`
  color: #00624a;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const LoginText = styled.span`
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: #2e3438;
  line-height: 1.4;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    flex-basis: 100%;
    order: -1;
    margin-bottom: 8px;
  }
`;

const LoginButton = styled.button`
  background: #00624a;
  border: none;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 24px;
  transition: background 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #004d3a;
  }

  &:active {
    background: #003d2e;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    width: 100%;
  }
`;

const skeletonPulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

const SkeletonCard = styled.div`
  align-items: center;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors?.greyBorder ?? '#e3e7e9'};
  display: grid;
  gap: 12px;
  grid-template-columns: 0.75fr 1fr;
  min-width: 0;
  padding: 16px;
  width: 100%;

  @media (min-width: 1280px) {
    gap: 20px;
    grid-template-columns: 1fr 1fr;
  }
`;

const SkeletonImage = styled.div`
  animation: ${skeletonPulse} 1s linear infinite;
  aspect-ratio: 286 / 124;
  background-color: ${({ theme }) => theme.colors?.grey?.[95] ?? '#e3e7e9'};
  border-radius: 8px;
  width: 100%;
`;

const SkeletonText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  width: 100%;
`;

const SkeletonLine = styled.div<{ $width: string }>`
  animation: ${skeletonPulse} 1s linear infinite;
  background-color: ${({ theme }) => theme.colors?.grey?.[95] ?? '#e3e7e9'};
  border-radius: 4px;
  height: 14px;
  width: ${({ $width }) => $width};
`;
